import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Edit2, Save, ShoppingBasket } from 'lucide-react';
import { Product } from '../../types/Product';
import { useProducts } from '../../context/ProductContext';
import { ProductSearch } from './ProductSearch';
import { ProductList } from './ProductList';
import { DeleteListButton } from './DeleteListButton';
import { MenuGenerator } from './MenuGenerator';
import { updateShoppingList } from '../../lib/firebase/firestore';

interface ShoppingListProps {
  list: {
    id: string;
    name: string;
    products: Product[];
    budget: number;
    dishCount: number;
  };
  onDeleted?: () => void;
  onUpdated?: () => void;
}

export const ShoppingList: React.FC<ShoppingListProps> = ({
  list,
  onDeleted,
  onUpdated
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(list.name);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>(list.products);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Initialize quantities from existing products
    const initialQuantities: Record<string, number> = {};
    list.products.forEach(product => {
      initialQuantities[product.id] = 1;
    });
    setQuantities(initialQuantities);
  }, [list.products]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateShoppingList(list.id, {
        name: editedName,
        products: selectedProducts
      });
      setIsEditing(false);
      onUpdated?.();
    } catch (error) {
      console.error('Error saving list:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleProductSelect = (product: Product) => {
    if (!selectedProducts.some(p => p.id === product.id)) {
      setSelectedProducts(prev => [...prev, product]);
      setQuantities(prev => ({ ...prev, [product.id]: 1 }));
      
      // Auto-save when adding products
      updateShoppingList(list.id, {
        products: [...selectedProducts, product]
      }).then(() => onUpdated?.());
    }
  };

  const handleRemoveProduct = (productId: string) => {
    setSelectedProducts(prev => {
      const updated = prev.filter(p => p.id !== productId);
      
      // Auto-save when removing products
      updateShoppingList(list.id, {
        products: updated
      }).then(() => onUpdated?.());
      
      return updated;
    });
    
    setQuantities(prev => {
      const { [productId]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleUpdateQuantity = (productId: string, quantity: number) => {
    if (quantity < 1) return;
    setQuantities(prev => ({ ...prev, [productId]: quantity }));
  };

  const totalPrice = selectedProducts.reduce((sum, product) => 
    sum + product.current_price * (quantities[product.id] || 1), 0
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-4"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ShoppingBasket className="text-orange-500" size={20} />
          {isEditing ? (
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              className="flex-1 px-3 py-1 border-2 border-orange-200 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
            />
          ) : (
            <h3 className="text-lg font-semibold">{list.name}</h3>
          )}
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={isEditing ? handleSave : () => setIsEditing(true)}
            disabled={isSaving}
            className="p-2 text-gray-500 hover:text-orange-500 transition-colors disabled:opacity-50"
          >
            {isEditing ? <Save size={18} /> : <Edit2 size={18} />}
          </motion.button>
          <DeleteListButton 
            listId={list.id}
            onDeleted={onDeleted}
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
        <span>Presupuesto: {list.budget}€</span>
        <span>Platos: {list.dishCount}</span>
      </div>

      <ProductSearch
        onProductSelect={handleProductSelect}
      />

      {selectedProducts.length > 0 ? (
        <div className="mt-6">
          <ProductList
            products={selectedProducts}
            quantities={quantities}
            onRemoveProduct={handleRemoveProduct}
            onUpdateQuantity={handleUpdateQuantity}
          />
          
          <div className="mt-4 p-4 bg-orange-50 rounded-lg flex justify-between items-center">
            <span className="font-medium">Total de la lista:</span>
            <span className="text-xl font-bold text-orange-600">
              {totalPrice.toFixed(2)}€
            </span>
          </div>

          <MenuGenerator
            products={selectedProducts}
            budget={list.budget}
            dishCount={list.dishCount}
          />
        </div>
      ) : (
        <div className="text-center py-8 bg-orange-50 rounded-lg">
          <ShoppingBasket className="mx-auto text-orange-300 mb-4" size={32} />
          <p className="text-gray-600">
            Añade productos a tu lista usando el buscador
          </p>
        </div>
      )}
    </motion.div>
  );
};